import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { NewsletterService } from '../../../_services/newsletter/newsletter.service';
import { AdminPageHeaderComponent } from '../../../_components/admin-page-header/admin-page-header.component';

interface DomainStat {
  domain: string;
  count: number;
}

@Component({
    selector: 'app-newsletter',
    imports: [FormsModule, AdminPageHeaderComponent],
    templateUrl: './newsletter.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './newsletter.component.scss'
})
export class NewsletterComponent implements OnInit {
  private newsletterService = inject(NewsletterService);

  emails: string[] = [];
  filteredEmails: string[] = [];
  topDomains: DomainStat[] = [];
  domainsCount = 0;

  private _search = '';
  copied = false;
  loading = true;

  get search(): string { return this._search; }
  set search(value: string) {
    this._search = value ?? '';
    this.applyFilter();
  }

  ngOnInit() {
    this.newsletterService.readAllNewsletters().subscribe({
      next: (data) => {
        this.emails = this.normalize(data);
        this.computeStats();
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.emails = [];
        this.loading = false;
      }
    });
  }

  private normalize(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    const out: string[] = [];
    for (const item of raw) {
      if (typeof item === 'string' && item.trim() !== '') {
        out.push(item.trim());
      }
    }
    return out;
  }

  private computeStats() {
    const map = new Map<string, number>();
    for (const e of this.emails) {
      const idx = e.indexOf('@');
      const d = idx >= 0 ? e.substring(idx + 1).toLowerCase() : 'inconnu';
      if (!d) continue;
      map.set(d, (map.get(d) ?? 0) + 1);
    }
    this.domainsCount = map.size;
    this.topDomains = Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([domain, count]) => ({ domain, count }));
  }

  private applyFilter() {
    const q = this._search.trim().toLowerCase();
    this.filteredEmails = q
      ? this.emails.filter(e => e.toLowerCase().includes(q))
      : this.emails.slice();
  }

  domainOf(email: string): string {
    const idx = email.indexOf('@');
    return idx >= 0 ? email.substring(idx + 1) : '—';
  }

  async copyAll() {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(this.filteredEmails.join(', '));
      this.copied = true;
      setTimeout(() => this.copied = false, 2000);
    } catch {
      // ignored
    }
  }

  exportCsv() {
    if (typeof document === 'undefined') return;
    const rows = ['email', ...this.filteredEmails];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
